/**
 * 公開資料画面 (/docs/:slug)。パスワード認証 + 比較表 + 全 13 タイプ + 5 テーマ + PDF 印刷ボタン。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §12
 */
import type { PublicDocumentView } from '@goenhub/shared';
import { httpsCallable } from 'firebase/functions';
import { Printer } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { fetchPublicDocument } from '@/hooks/useDocuments';
import { useLiffLineUid } from '@/hooks/useLiffLineUid';
import { usePublicPageMode } from '@/hooks/usePublicPageMode';
import { functions } from '@/lib/firebase';
import { getOrCreateVisitorId } from '@/lib/visitor';
import { DocumentSectionRenderer } from '@/pages/documents/components/DocumentSectionRenderer';

type State =
  | { kind: 'loading' }
  | { kind: 'password_required' }
  | { kind: 'wrong_password' }
  | { kind: 'expired' }
  | { kind: 'not_found' }
  | { kind: 'ok'; document: PublicDocumentView };

export function PublicDocumentPage() {
  usePublicPageMode();
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [password, setPassword] = useState('');

  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid');
  const lid = searchParams.get('lid');
  const src = searchParams.get('src');
  const ref = searchParams.get('ref');
  const queryCampaignId = searchParams.get('campaignId');

  const liff = useLiffLineUid(!!lid);
  const effectiveLid = lid || liff.lineUid;
  const effectiveSrc = src || (liff.isInClient ? 'line' : null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    fetchPublicDocument(slug).then((res) => {
      if (cancelled) return;
      if (res.status === 'ok') setState({ kind: 'ok', document: res.document });
      else setState({ kind: res.status === 'password_required' ? 'password_required' : res.status });
    });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // audience_events に document_view を記録（一度だけ、認証通過後に limit）
  const documentId = state.kind === 'ok' ? state.document.documentId : null;
  useEffect(() => {
    if (!documentId) return;
    if (!liff.ready) return;
    const visitorId = getOrCreateVisitorId();
    const fn = httpsCallable(functions, 'recordDocumentView');
    fn({
      documentId,
      cid: cid || null,
      lid: effectiveLid || null,
      src: effectiveSrc,
      ref: ref || null,
      visitorId,
      campaignId: queryCampaignId || null,
      metadata: {
        userAgent: navigator.userAgent.slice(0, 200),
        referrer: document.referrer.slice(0, 200) || null,
        liffEnriched: !lid && !!liff.lineUid,
      },
    }).catch(() => {
      // 計測失敗はユーザー体験に影響させない
    });
  }, [
    documentId,
    cid,
    effectiveLid,
    effectiveSrc,
    ref,
    queryCampaignId,
    liff.ready,
    liff.lineUid,
    lid,
  ]);

  const handleSubmitPassword = async () => {
    if (!slug) return;
    setState({ kind: 'loading' });
    const res = await fetchPublicDocument(slug, password);
    if (res.status === 'ok') setState({ kind: 'ok', document: res.document });
    else if (res.status === 'password_required' || res.status === 'wrong_password') {
      setState({ kind: 'wrong_password' });
    } else setState({ kind: res.status });
  };

  const handlePrint = () => {
    window.print();
  };

  if (state.kind === 'loading') return <Spinner />;
  if (state.kind === 'not_found') {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">資料が見つかりません</h1>
        <p>URL をご確認ください。</p>
      </div>
    );
  }
  if (state.kind === 'expired') {
    return (
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-xl font-semibold mb-2">資料の閲覧期限が切れています</h1>
      </div>
    );
  }
  if (state.kind === 'password_required' || state.kind === 'wrong_password') {
    return (
      <div className="p-8 max-w-md mx-auto">
        <h1 className="text-xl font-semibold mb-4">パスワードを入力してください</h1>
        {state.kind === 'wrong_password' && (
          <p className="text-sm text-red-500 mb-2">
            パスワードが正しくありません。
          </p>
        )}
        <div className="flex gap-2">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmitPassword();
            }}
            className="flex-1 px-3 py-2 text-sm bg-[var(--bg-elevated)] border border-[var(--border)] rounded-sm"
          />
          <Button type="button" variant="primary" onClick={handleSubmitPassword}>
            開く
          </Button>
        </div>
      </div>
    );
  }

  const doc = state.document;
  const theme = doc.theme ?? 'light';
  return (
    <PublicDocumentThemeScope theme={theme}>
      <div
        data-doc-theme={theme}
        className="min-h-screen"
        style={{
          background: 'var(--bg-base)',
          color: 'var(--text-primary)',
        }}
      >
      {/* 上部 印刷ボタン (印刷時は非表示) */}
      <div className="doc-no-print sticky top-0 z-10 backdrop-blur-sm border-b border-[var(--border)]"
           style={{ background: 'color-mix(in srgb, var(--bg-base) 85%, transparent)' }}>
        <div className="max-w-3xl mx-auto px-8 py-3 flex items-center justify-between gap-3">
          <div className="text-sm font-medium truncate">{doc.title}</div>
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={handlePrint}
            title="ブラウザの印刷ダイアログから PDF として保存できます"
          >
            <Printer className="size-3.5 mr-1" />
            PDFで保存・印刷
          </Button>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-8 space-y-8">
        <h1 className="text-3xl font-bold">{doc.title}</h1>
        {doc.sections
          .filter((s) => !s.hidden)
          .map((s, i) => (
            <DocumentSectionRenderer key={s.id || i} section={s} />
          ))}
        <footer className="doc-no-print pt-8 mt-12 border-t border-[var(--border)] text-center">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={handlePrint}
          >
            <Printer className="size-3.5 mr-1" />
            PDFで保存・印刷
          </Button>
        </footer>
      </div>
      </div>
    </PublicDocumentThemeScope>
  );
}

const DARK_DOC_THEMES = new Set(['dark', 'vibrant']);

/**
 * 公開資料ページ閲覧中、admin の ThemeProvider が html に付けた
 * `data-theme` を資料のテーマに合わせて上書きする。
 *
 * 背景:
 * - Tailwind の darkMode 設定は `['class', '[data-theme="dark"]']`
 * - admin の ThemeProvider は閲覧者の OS ダークモードに合わせて
 *   `data-theme="dark"` を html に付ける（未認証ユーザでも発動）
 * - これにより、light テーマの資料を見ても `dark:prose-invert` 等が
 *   全箇所で発火し、白背景に白文字となって視認性が崩壊する
 *
 * このコンポーネントは資料のテーマに応じて html の data-theme を
 * 上書きし、アンマウント時に元の値を復元する。
 */
function PublicDocumentThemeScope({
  theme,
  children,
}: {
  theme: string;
  children: ReactNode;
}) {
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.getAttribute('data-theme');
    const next = DARK_DOC_THEMES.has(theme) ? 'dark' : 'light';
    html.setAttribute('data-theme', next);
    return () => {
      if (previous === null) html.removeAttribute('data-theme');
      else html.setAttribute('data-theme', previous);
    };
  }, [theme]);
  return <>{children}</>;
}
